import { useId, useState, type KeyboardEvent, type PointerEvent } from "react";
import {
  DEFAULT_DAY_HOURS, DEFAULT_DAY_RULE, WEEKDAY_NAMES, clockLabel, isDefaultDay, parseClock, protectedRange,
  type DayHours, type DayRule, type ProtectedHours, type WeekHours, type Weekday,
} from "../../btb/protected-hours";
import { RULE_STEPS } from "../../btb/constants";
import { EDITOR_WEEKDAYS, clockAtFraction, dayWords, defaultRangeFor, openHoursText, railFraction, summarizeProtectedHours } from "../lib/protected-hours";
import { Button, Help, Toggle, cx } from "./ui";
import { HELP } from "./RuleExplainer";

const STEP = RULE_STEPS.protectedHoursStepMinutes;
const DAY_END = 1440;
const toClock = (m: number) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
const pct = (f: number) => `${f * 100}%`;

// Figma rail: a 13px pale-green pill with a green edge; the protected band is blush with a crimson edge; off is dashed grey.
const TRACK = "absolute inset-x-0 top-1/2 h-[13px] -translate-y-1/2 rounded-full border";
const GREEN = "border-hs-rail-open-edge bg-hs-rail-open";
const OFF = "border-dashed border-hs-border-strong bg-hs-surface-alt";
const BAND = "absolute top-1/2 h-[13px] -translate-y-1/2 rounded-full border border-hs-primary bg-hs-primary-soft";

/** One round, 44px-target handle. Keyboard steps 30 min; a drag snaps through `clockAtFraction`. */
function Handle({ day, edge, minutes, fraction, hours, bounds, onMove }: {
  day: Weekday; edge: "start" | "end"; minutes: number; fraction: number; hours: DayHours;
  bounds: { min: number; max: number }; onMove: (minutes: number) => void;
}) {
  const move = (m: number) => {
    const next = Math.min(bounds.max, Math.max(bounds.min, m));
    if (next !== minutes) onMove(next);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    const to = e.key === "ArrowLeft" || e.key === "ArrowDown" ? minutes - STEP
      : e.key === "ArrowRight" || e.key === "ArrowUp" ? minutes + STEP
      : e.key === "Home" ? (edge === "start" ? 0 : bounds.min)
      : e.key === "End" ? (edge === "end" ? DAY_END : bounds.max)
      : null;
    if (to === null) return;
    e.preventDefault();
    move(to);
  };
  const onPointerDown = (e: PointerEvent<HTMLButtonElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    e.currentTarget.focus();
  };
  const onPointerMove = (e: PointerEvent<HTMLButtonElement>) => {
    if (!e.currentTarget.hasPointerCapture(e.pointerId)) return;
    const rail = e.currentTarget.parentElement!.getBoundingClientRect();
    if (rail.width <= 0) return;
    move(parseClock(clockAtFraction((e.clientX - rail.left) / rail.width, hours)) ?? minutes);
  };
  const onPointerUp = (e: PointerEvent<HTMLButtonElement>) => {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
  };
  return (
    <button type="button" role="slider" aria-valuemin={0} aria-valuemax={DAY_END} aria-valuenow={minutes} aria-valuetext={clockLabel(minutes)}
      aria-label={`${WEEKDAY_NAMES[day]}: protected hours ${edge}, ${clockLabel(minutes)}`}
      onKeyDown={onKeyDown} onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp}
      className="absolute top-1/2 flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 touch-none items-center justify-center rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-hs-primary"
      style={{ left: pct(fraction) }}>
      <span aria-hidden className="h-4 w-4 rounded-full border-2 border-hs-primary bg-white shadow-sm" />
    </button>
  );
}

/** The day's rail across its open hours: green, green with a blush band and two handles, or dashed when off. */
function Rail({ day, rule, hours, onChange }: { day: Weekday; rule: DayRule; hours: DayHours; onChange: (rule: DayRule) => void }) {
  const range = protectedRange(rule);
  if (!rule.btb || !range) return <div className="relative mx-5 h-11"><div className={cx(TRACK, rule.btb ? GREEN : OFF)} /></div>;
  const from = railFraction(rule.protectFrom!, hours);
  const to = railFraction(rule.protectTo!, hours);
  const set = (f: number, t: number) => onChange({ btb: true, protectFrom: toClock(f), protectTo: toClock(t) });
  return (
    <div className="relative mx-5 h-11">
      <div className={cx(TRACK, GREEN)} />
      <div className={BAND} style={{ left: pct(from), width: pct(to - from) }} />
      <Handle day={day} edge="start" minutes={range.from} fraction={from} hours={hours} bounds={{ min: 0, max: range.to - STEP }} onMove={(m) => set(m, range.to)} />
      <Handle day={day} edge="end" minutes={range.to} fraction={to} hours={hours} bounds={{ min: range.from + STEP, max: DAY_END }} onMove={(m) => set(range.from, m)} />
    </div>
  );
}

const ROW = "grid grid-cols-1 gap-x-4 gap-y-1 px-5 py-3 sm:grid-cols-[9.5rem_8rem_minmax(0,1fr)_9rem] sm:items-center";

function DayRow({ day, rule, hours, isDefaultHours, onChange }: { day: Weekday; rule: DayRule; hours: DayHours; isDefaultHours: boolean; onChange: (rule: DayRule) => void }) {
  const name = WEEKDAY_NAMES[day];
  const words = dayWords(rule, hours);
  const hasRange = rule.btb && protectedRange(rule) !== null;
  return (
    <div role="group" aria-label={name} className={cx(ROW, "border-t border-hs-border", !rule.btb && "text-hs-text-muted")}>
      <div className="flex min-h-11 items-center gap-2">
        <Toggle checked={rule.btb} label={`BTB blocks on ${name}`} onChange={() => onChange({ btb: !rule.btb, protectFrom: null, protectTo: null })} />
        <span className={cx("text-sm font-medium", rule.btb ? "text-hs-text" : "text-hs-text-muted")}>{name}</span>
      </div>
      <div className={cx("text-sm font-semibold", isDefaultHours ? "text-hs-text-muted" : "text-hs-text")}>{openHoursText(hours)}{isDefaultHours ? " (default)" : ""}</div>
      <div className="min-w-0">
        <Rail day={day} rule={rule} hours={hours} onChange={onChange} />
        <p className="text-xs font-medium text-hs-text-muted"><span>{rule.btb ? words.caption : `BTB is off on ${name}s`}</span> · <span>{words.result}</span></p>
      </div>
      <div className="flex min-h-11 items-center sm:justify-end">
        {rule.btb && !hasRange && (
          <Button variant="secondary" size="sm" onClick={() => onChange({ btb: true, ...defaultRangeFor(hours) })}>+ Protect hours</Button>
        )}
        {hasRange && <Button variant="ghost" size="sm" onClick={() => onChange({ ...DEFAULT_DAY_RULE })}>Clear</Button>}
      </div>
    </div>
  );
}

const LEGEND: { track: string; band?: boolean; text: string }[] = [
  { track: GREEN, text: "On, nothing protected (default): BTB blocks any time the salon is open." },
  { track: GREEN, band: true, text: "On, with protected hours: no BTB blocks inside the pink band. Drag its ends to change it." },
  { track: OFF, text: "Off: no BTB blocks that day." },
];

/**
 * Per-weekday editor for protected hours, as a section card whose whole header is the open/close
 * button. Collapsed on every mount; the subtitle says the rule while closed.
 */
export function ProtectedHoursEditor({ value, onChange, hours, hoursSource }: {
  value: ProtectedHours; onChange: (next: ProtectedHours) => void; hours: WeekHours | null; hoursSource: "boulevard" | "default";
}) {
  const [open, setOpen] = useState(false);
  const bodyId = useId();
  const summary = summarizeProtectedHours(value);
  const isDefault = EDITOR_WEEKDAYS.every((d) => isDefaultDay(value[d]));
  return (
    <section className="rounded-2xl border border-hs-border bg-hs-surface">
      <header className="flex flex-wrap items-center justify-between gap-3 p-6">
        <button type="button" aria-expanded={open} aria-controls={bodyId} onClick={() => setOpen((o) => !o)}
          className="flex min-w-0 flex-1 items-center gap-3 rounded-lg text-left">
          <span className="min-w-0">
            <span className="block text-lg font-bold text-hs-text">Rules By Day</span>
            <span className="mt-1 flex flex-wrap items-center gap-2 text-sm font-medium text-hs-text-muted">
              {isDefault && <span aria-hidden className="h-2 w-2 rounded-full bg-hs-success" />}
              <span>{summary}</span>
            </span>
          </span>
          <svg aria-hidden viewBox="0 0 20 20" className={cx("ml-auto h-5 w-5 shrink-0 text-hs-text-muted transition-transform", open && "rotate-180")} fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 7.5l5 5 5-5" />
          </svg>
        </button>
        <Help label="Why protect hours?">{HELP.protectedHours}</Help>
      </header>
      {open && (
        <div id={bodyId} className="border-t border-hs-border">
          <div aria-hidden className={cx(ROW, "hidden bg-hs-surface-alt py-3 text-xs font-semibold text-hs-text-muted sm:grid")}>
            <span>Day</span><span>Open</span><span>When BTB blocks may be placed</span><span>Result</span>
          </div>
          {EDITOR_WEEKDAYS.map((d) => (
            <DayRow key={d} day={d} rule={value[d]} hours={hours?.[d] ?? DEFAULT_DAY_HOURS} isDefaultHours={hoursSource === "default" || !hours?.[d]}
              onChange={(rule) => onChange({ ...value, [d]: rule })} />
          ))}
          <div className="space-y-3 border-t border-hs-border px-6 py-5 text-sm font-medium text-hs-text-muted">
            <div className="space-y-2 rounded-xl border border-hs-border bg-hs-surface-alt p-4">
              <p className="font-semibold text-hs-text">Three states</p>
              {LEGEND.map((l) => (
                <div key={l.text} className="flex items-center gap-3">
                  <span aria-hidden className="relative h-4 w-16 shrink-0">
                    <span className={cx(TRACK, l.track)} />
                    {l.band && <span className={BAND} style={{ left: "30%", width: "40%" }} />}
                  </span>
                  <span>{l.text}</span>
                </div>
              ))}
            </div>
            <p className="text-xs">Tip: set the range a little past where a morning shift ends and an evening shift starts. A BTB block that only touches the range is still placed; one that overlaps it, even by a few minutes, is left out. Preview shows the effect for any day.</p>
          </div>
        </div>
      )}
    </section>
  );
}
